import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_NOTIFICATIONS } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  GeneralDisplaySilentModeIssuesVisibilityAlertAction,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  columns,
  convertDbValueToFormValue,
  convertFormValueToDbValue,
  GeneralNotificationsDisplayPopupsColumn,
  GeneralNotificationsDisplayPopupsKey,
  rows,
} from './complex-selection-group-config';
import { GzSelectableTableRowGroupWebComponent } from '../../../../../../../ui/gzSelectableTableRowGroup';
import { Feature } from '@nirvana/shared/constants';
import {
  GeneralNotificationsFormValue,
  GeneralNotificationsSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.types';
import {
  getGeneralNotificationsDefaultValue,
  getGeneralDisplaySilentModeIssuesVisibilityAlertActionFormOptions,
  getGeneralNotificationsSearchableKeywords,
  MAX_ISSUE_VISIBILITY_SHOW_AFTER,
  MIN_ISSUE_VISIBILITY_SHOW_AFTER,
} from '../../../../utils/uiSettings/general/general.values';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { SelectionType } from '../../../../../../../ui/gzSelectableTableRowGroup/types';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';
import { classMap } from 'lit/directives/class-map.js';
import { InfoBoxSeverity } from '../../../../../../../ui/gzInfoBox/types';
import { GzInfoBoxWebComponent } from '../../../../../../../ui/gzInfoBox';

@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_NOTIFICATIONS)
export class GzGeneralNotificationsWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  GeneralNotificationsFormValue,
  GeneralNotificationsSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.GENERAL,
    sectionId: SectionId.GENERAL_NOTIFICATIONS,
  };
  protected searchableKeywords = getGeneralNotificationsSearchableKeywords();
  private isPatchVisible = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PATCH_MANAGEMENT_VISIBLE]);

  @query('#general-display-silent-mode-disable-field')
  private silentModeDisableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-silent-mode-enabled-warning')
  private silentModeEnabledWarning: GzInfoBoxWebComponent;
  @query('#general-display-notifications-content')
  private notificationsContent: HTMLElement;
  @query('#general-display-show-icon-enable-field')
  private showIconEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-alert-popups-enable-field')
  private displayAlertsPopupsEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-show-popups-enable-field')
  private showPopupsEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-show-popups-settings-field')
  private showPopupsSettingsField: GzFormFieldWebComponent<
    GzSelectableTableRowGroupWebComponent<GeneralNotificationsDisplayPopupsKey, GeneralNotificationsDisplayPopupsColumn>
  >;
  @query('#general-display-issue-visibility-enable-field')
  private issueVisibilityEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-issue-visibility-content')
  private issueVisibilityContent: HTMLElement;
  @query('#general-display-issue-visibility-general-field')
  private issueVisibilityGeneralField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-issue-visibility-general-content')
  private issueVisibilityGeneralContent: HTMLElement;
  @query('#general-display-issue-visibility-general-restart-notifications-field')
  private issueVisibilityGeneralRestartNotificationsField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#general-display-issue-visibility-general-connection-notifications-field')
  private issueVisibilityConnectionNotificationsField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#general-display-issue-visibility-antimalware-field')
  private issueVisibilityAntimalwareField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-issue-visibility-antimalware-content')
  private issueVisibilityAntimalwareContent: HTMLElement;
  @query('#general-display-issue-visibility-antimalware-on-access-notifications-field')
  private issueVisibilityAntimalwareOnAccessNotificationsField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#general-display-issue-visibility-antimalware-on-demand-notifications-field')
  private issueVisibilityAntimalwareOnDemandNotificationsField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#general-display-issue-visibility-antimalware-show-after-field')
  private issueVisibilityAntimalwareShowAfterField: GzFormFieldWebComponent<GzInputNumberWebComponent>;
  @query('#general-display-issue-visibility-antimalware-restart-notifications-field')
  private issueVisibilityAntimalwareRestartNotificationsField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#general-display-issue-visibility-firewall-field')
  private issueVisibilityFirewallField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-issue-visibility-content-control-field')
  private issueVisibilityContentControlField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-issue-visibility-update-field')
  private issueVisibilityUpdateField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-issue-visibility-update-content')
  private issueVisibilityUpdateContent: HTMLElement;
  @query('#general-display-issue-visibility-update-restart-notifications-field')
  private issueVisibilityUpdateRestartNotificationsField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#general-display-restart-enable-field')
  private restartEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-restart-content')
  private restartContent: HTMLElement;
  @query('#general-display-restart-update-field')
  private restartUpdateField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#general-display-restart-patch-field')
  private restartPatchField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <gz-form-field id="general-display-silent-mode-disable-field" class="mbw-1" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[GeneralNotificationsSearchableKeywords.Notifications].label}"
        gz-form-input
        @toggleChange="${e => this.toggleSilentMode(!e.detail.checked)}"
        ><span
          >${this.searchableKeywords[GeneralNotificationsSearchableKeywords.Notifications].translatedLabel}</span
        ></gz-toggle
      >
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      id="general-display-silent-mode-disable-text"
      fontSize="${FontSizes.PARAGRAPH_12}"
      fontWeight="${FontWeights.NORMAL}"
      searchable-label="${this.searchableKeywords[GeneralNotificationsSearchableKeywords.SilentMode].label}"
      >${translateFn(
        'Customize how the Bitdefender security agent displays notifications on the endpoint. Disabling notifications activates Silent Mode.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    const isFirewallProtectionLevelImpactedBySilentModeChange =
      PolicyPanelManager.getInstance().isSilentModeDependentOfFirewallProtectionLevelValue();
    const isFirewallProtectionLevelImpactedByDisplayAlertPopusChange =
      PolicyPanelManager.getInstance().isDisplayAlertPopupsDependentOfFirewallProtectionLevelValue();
    return html`
      <!-- General - Notifications - Silent Mode -->
      <div id="general-display-silent-mode-content">
        <gz-info-box
          id="general-display-silent-mode-enabled-warning"
          severity="${InfoBoxSeverity.WARNING}"
          class="hidden mt-2"
        >
          ${translateFn('Silent Mode is enabled.')}
        </gz-info-box>

        <gz-info-box
          id="general-display-silent-mode-enabled-change-firewall-warning"
          severity="${InfoBoxSeverity.INFORMATIVE}"
          class="${classMap({ hidden: !isFirewallProtectionLevelImpactedBySilentModeChange })} mt-2"
        >
          ${translateFn(
            'To keep Firewall asking for permission, do not enable Silent Mode. Silent Mode blocks all prompts, which conflicts with your current protection level set to "Ruleset and ask" or "Ruleset, known files and ask" in the Firewall > Rules section.'
          )}
        </gz-info-box>
      </div>

      <div id="general-display-notifications-content">
        <!-- General - Notifications - Show icon -->
        <div id="general-display-show-icon-content" class="mt-4">
          <gz-form-field id="general-display-show-icon-enable-field" class="mbw-1">
            <gz-toggle gz-form-input @toggleChange="${this.toggleShowIcon}" class="display-flex">
              <gz-title
                fontSize="${FontSizes.PARAGRAPH_11}"
                fontWeight="${FontWeights.SEMIBOLD}"
                class="toggle-subtitle"
                searchable-label="${this.searchableKeywords[
                  GeneralNotificationsSearchableKeywords.ShowIconInNotificationArea
                ].label}"
                >${this.searchableKeywords[GeneralNotificationsSearchableKeywords.ShowIconInNotificationArea]
                  .translatedLabel}</gz-title
              >
            </gz-toggle>
          </gz-form-field>

          <gz-text
            id="general-display-show-icon-enable-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${translateFn('A system reboot may be required to apply this setting.')}
          </gz-text>
        </div>

        <!-- General - Notifications - Display alert popups -->
        <div id="general-display-alert-popups-content" class="mt-4">
          <gz-form-field id="general-display-alert-popups-enable-field" class="mbw-1">
            <gz-toggle gz-form-input @toggleChange="${this.toggleDisplayAlertPopups}" class="display-flex">
              <gz-title
                fontSize="${FontSizes.PARAGRAPH_11}"
                fontWeight="${FontWeights.SEMIBOLD}"
                class="toggle-subtitle"
                searchable-label="${this.searchableKeywords[GeneralNotificationsSearchableKeywords.DisplayAlertsPopups]
                  .label}"
                >${this.searchableKeywords[GeneralNotificationsSearchableKeywords.DisplayAlertsPopups]
                  .translatedLabel}</gz-title
              >
            </gz-toggle>
          </gz-form-field>

          <gz-text
            id="general-display-alert-popups-enable-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${translateFn(
              'Alert pop-ups require user action. Disabling this option results in Bitdefender automatically applying the recommended action on the endpoint.'
            )}
          </gz-text>

          <gz-info-box
            id="general-display-alert-popups-enabled-change-firewall-warning"
            severity="${InfoBoxSeverity.INFORMATIVE}"
            class="${classMap({ hidden: !isFirewallProtectionLevelImpactedByDisplayAlertPopusChange })} mt-2"
          >
            ${translateFn(
              'To keep Firewall asking for permission, do not disable alert pop-ups. Disabling them automatically changes your current protection level from "Ruleset and ask" or "Ruleset, known files and ask" to "Ruleset, known files and allow" in the Firewall > Rules section.'
            )}
          </gz-info-box>
        </div>

        <!-- General - Notifications - Display notifications popups -->
        <div id="general-display-show-popups-content" class="mt-4">
          <gz-form-field id="general-display-show-popups-enable-field" class="mbw-1">
            <gz-toggle gz-form-input @toggleChange="${this.toggleShowPopups}" class="display-flex">
              <gz-title
                fontSize="${FontSizes.PARAGRAPH_11}"
                fontWeight="${FontWeights.SEMIBOLD}"
                class="toggle-subtitle"
                searchable-label="${this.searchableKeywords[
                  GeneralNotificationsSearchableKeywords.DisplayNotificationPopups
                ].label}"
                >${this.searchableKeywords[GeneralNotificationsSearchableKeywords.DisplayNotificationPopups]
                  .translatedLabel}</gz-title
              >
            </gz-toggle>
          </gz-form-field>

          <gz-text
            id="general-display-show-popups-enable-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${translateFn(
              'Notifications pop-ups provide endpoint users with information on critical security events, including malware detection and remediation. These notifications automatically expire within seconds, without user interaction.'
            )}
          </gz-text>

          <gz-form-field class="helper-text mt-2" id="general-display-show-popups-settings-field">
            <gz-selectable-table-row-group
              gz-form-input
              .selectionType=${SelectionType.CHECKBOX}
              .defaultColumn=${[]}
              allowFullColumnSelection
              .rows="${rows()}"
              .columns="${columns()}"
            ></gz-selectable-table-row-group>
          </gz-form-field>
        </div>

        <!-- General - Notifications - Endpoint issues visibility -->
        <div class="mt-4">
          <gz-form-field id="general-display-issue-visibility-enable-field" class="mbw-1">
            <gz-toggle gz-form-input @toggleChange="${this.toggleIssueVisibility}" class="display-flex">
              <gz-title
                fontSize="${FontSizes.PARAGRAPH_11}"
                fontWeight="${FontWeights.SEMIBOLD}"
                class="toggle-subtitle"
                searchable-label="${this.searchableKeywords[
                  GeneralNotificationsSearchableKeywords.EndpointIssuesVisibility
                ].label}"
                >${this.searchableKeywords[GeneralNotificationsSearchableKeywords.EndpointIssuesVisibility]
                  .translatedLabel}</gz-title
              >
            </gz-toggle>
          </gz-form-field>

          <gz-text
            id="general-display-issue-visibility-enable-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${translateFn(
              'Status alerts notify endpoint users of current security issues within specified categories, such as disabled Firewall or a pending system restart for update completion. When issues are found, the Bitdefender icon changes. The agent GUI displays warning messages and adjusts the status color to show issue severity.'
            )}
          </gz-text>

          <div id="general-display-issue-visibility-content" class="helper-text mt-2">
            <!-- General - Notifications - Endpoint issues visibility - General -->
            <gz-form-field id="general-display-issue-visibility-general-field">
              <gz-checkbox
                label="${translateFn('General')}"
                gz-form-input
                @change="${this.toggleIssueVisibilityGeneralSection}"
              ></gz-checkbox>
            </gz-form-field>
            <div id="general-display-issue-visibility-general-content" class="helper-text">
              <gz-form-field
                id="general-display-issue-visibility-general-restart-notifications-field"
                class="mbw-1"
                label="${translateFn('Restart upon agent installation')}"
              >
                <gz-dropdown
                  gz-form-input
                  .options=${getGeneralDisplaySilentModeIssuesVisibilityAlertActionFormOptions()}
                  .value=${GeneralDisplaySilentModeIssuesVisibilityAlertAction.SHOW_AS_WARNING}
                ></gz-dropdown>
              </gz-form-field>
              <gz-text
                id="general-display-issue-visibility-general-restart-notifications-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText"
                >${translateFn(
                  'This notification occurs when a system restart is required during or after agent installation.'
                )}
              </gz-text>

              <gz-form-field
                id="general-display-issue-visibility-general-connection-notifications-field"
                class="mbw-1 mt-2"
                label="${translateFn('Cloud Services connectivity')}"
              >
                <gz-dropdown
                  gz-form-input
                  .options=${getGeneralDisplaySilentModeIssuesVisibilityAlertActionFormOptions()}
                  .value=${GeneralDisplaySilentModeIssuesVisibilityAlertAction.SHOW_AS_WARNING}
                ></gz-dropdown>
              </gz-form-field>
              <gz-text
                id="general-display-issue-visibility-general-connection-notifications-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText"
                >${translateFn(
                  'This notification occurs when the agent cannot connect to Bitdefender Cloud Services. If "Do not show" is selected, events are not displayed in the agent GUI or reported to the GravityZone console. This option negatively affects endpoint protection.'
                )}
              </gz-text>
            </div>

            <!-- General - Notifications - Endpoint issues visibility - Antimalware -->
            <gz-form-field id="general-display-issue-visibility-antimalware-field" class="mt-2">
              <gz-checkbox
                label="${translateFn('Antimalware')}"
                gz-form-input
                @change="${this.toggleIssueVisibilityAntimalwareSection}"
              ></gz-checkbox>
            </gz-form-field>
            <div id="general-display-issue-visibility-antimalware-content" class="helper-text">
              <gz-form-field
                id="general-display-issue-visibility-antimalware-on-access-notifications-field"
                class="mbw-1"
                label="${translateFn('On-access Scanning')}"
              >
                <gz-dropdown
                  gz-form-input
                  .options=${getGeneralDisplaySilentModeIssuesVisibilityAlertActionFormOptions()}
                  .value=${GeneralDisplaySilentModeIssuesVisibilityAlertAction.SHOW_AS_WARNING}
                ></gz-dropdown>
              </gz-form-field>
              <gz-text
                id="general-display-issue-visibility-antimalware-on-access-notifications-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText"
                >${translateFn(
                  'This notification occurs when on-access file scanning is active, but many local files are skipped.'
                )}
              </gz-text>

              <div class="df mt-2">
                <gz-form-field
                  id="general-display-issue-visibility-antimalware-on-demand-notifications-field"
                  class="mbw-1"
                  label="${translateFn('On-demand Scanning')}"
                >
                  <gz-dropdown
                    gz-form-input
                    .options=${getGeneralDisplaySilentModeIssuesVisibilityAlertActionFormOptions()}
                    .value=${GeneralDisplaySilentModeIssuesVisibilityAlertAction.SHOW_AS_WARNING}
                    @selectionChanged="${e =>
                      toggleDisabled(
                        this.issueVisibilityAntimalwareShowAfterField,
                        e.detail.value === GeneralDisplaySilentModeIssuesVisibilityAlertAction.DO_NOT_SHOW
                      )}"
                  ></gz-dropdown>
                </gz-form-field>

                <gz-form-field
                  id="general-display-issue-visibility-antimalware-show-after-field"
                  class="mbw-1 ml-5"
                  label="${translateFn('Show after (days)')}"
                >
                  <gz-input-number
                    gz-form-input
                    inputWidth="${InputWidthValues.MEDIUM}"
                    min="${MIN_ISSUE_VISIBILITY_SHOW_AFTER}"
                    max="${MAX_ISSUE_VISIBILITY_SHOW_AFTER}"
                  ></gz-input-number>
                </gz-form-field>
              </div>
              <gz-text
                id="general-display-issue-visibility-antimalware-show-after-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText"
                >${translateFn(
                  'This notification occurs when a certain number of days have passed since the last full scan has been performed on the endpoint.'
                )}
              </gz-text>

              <gz-form-field
                id="general-display-issue-visibility-antimalware-restart-notifications-field"
                class="mbw-1 mt-2"
                label="${translateFn('Restart upon remediation')}"
              >
                <gz-dropdown
                  gz-form-input
                  .options=${getGeneralDisplaySilentModeIssuesVisibilityAlertActionFormOptions()}
                  .value=${GeneralDisplaySilentModeIssuesVisibilityAlertAction.SHOW_AS_WARNING}
                ></gz-dropdown>
              </gz-form-field>
              <gz-text
                id="general-display-issue-visibility-antimalware-restart-notifications-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText"
                >${translateFn(
                  'This notification occurs whenever a system restart is required to complete an antimalware remediation action.'
                )}
              </gz-text>
            </div>

            <!-- General - Notifications - Endpoint issues visibility - Firewall -->
            <gz-form-field id="general-display-issue-visibility-firewall-field" class="mt-2 mbw-1">
              <gz-checkbox
                label="${translateFn('Firewall')}"
                gz-form-input
                @change="${this.toggleIssueVisibilityFirewallSection}"
              ></gz-checkbox>
            </gz-form-field>
            <gz-text
              id="general-display-issue-visibility-firewall-text"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText"
              >${translateFn('This notification occurs when the Firewall module is disabled.')}
            </gz-text>

            <!-- General - Notifications - Endpoint issues visibility - Content Control -->
            <gz-form-field id="general-display-issue-visibility-content-control-field" class="mbw-1 mt-2">
              <gz-checkbox
                label="${translateFn('Content Control')}"
                gz-form-input
                @change="${this.toggleIssueVisibilityContentControlSection}"
              ></gz-checkbox>
            </gz-form-field>
            <gz-text
              id="general-display-issue-visibility-content-control-text"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText"
              >${translateFn('This notification occurs when the Content Control module is disabled.')}
            </gz-text>

            <!-- General - Notifications - Endpoint issues visibility - Update -->
            <gz-form-field id="general-display-issue-visibility-update-field" class="mt-2">
              <gz-checkbox
                label="${translateFn('Update')}"
                gz-form-input
                @change="${this.toggleIssueVisibilityUpdateSection}"
              ></gz-checkbox>
            </gz-form-field>
            <div id="general-display-issue-visibility-update-content" class="helper-text">
              <gz-form-field
                id="general-display-issue-visibility-update-restart-notifications-field"
                class="mbw-1"
                label="${translateFn('Restart upon update')}"
              >
                <gz-dropdown
                  gz-form-input
                  .options=${getGeneralDisplaySilentModeIssuesVisibilityAlertActionFormOptions()}
                  .value=${GeneralDisplaySilentModeIssuesVisibilityAlertAction.SHOW_AS_WARNING}
                ></gz-dropdown>
              </gz-form-field>
              <gz-text
                id="general-display-issue-visibility-update-restart-notifications-text"
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="infoText"
                >${translateFn(
                  'This notification occurs whenever a system restart is required to complete an update operation.'
                )}
              </gz-text>
            </div>
          </div>
        </div>

        <!-- General - Notifications - Endpoint restart notification -->
        <div class="mt-4">
          <gz-form-field id="general-display-restart-enable-field" class="mbw-1">
            <gz-toggle gz-form-input @toggleChange="${this.toggleRestartNotification}" class="display-flex">
              <gz-title
                fontSize="${FontSizes.PARAGRAPH_11}"
                fontWeight="${FontWeights.SEMIBOLD}"
                class="toggle-subtitle"
                searchable-label="${this.searchableKeywords[
                  GeneralNotificationsSearchableKeywords.EndpointRestartNotification
                ].label}"
                >${this.searchableKeywords[GeneralNotificationsSearchableKeywords.EndpointRestartNotification]
                  .translatedLabel}</gz-title
              >
            </gz-toggle>
          </gz-form-field>

          <gz-text
            id="general-display-restart-enable-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            >${translateFn(
              'This notification occurs whenever a system restart is required due to agent updates or changes made to the endpoint by Bitdefender. It includes a postpone option, allowing users to schedule the restart. When postponed, the notification reappears until the endpoint restarts.'
            )}
          </gz-text>

          <div id="general-display-restart-content" class="helper-text">
            <gz-form-field id="general-display-restart-update-field" class="mt-2">
              <gz-checkbox
                label="${translateFn('Update')}"
                gz-form-input
                @change="${this.checkRestartNotificationUpdateStatus}"
              ></gz-checkbox>
            </gz-form-field>
            <gz-form-field
              id="general-display-restart-patch-field"
              class="mbw-1 ${classMap({ hidden: !this.isPatchVisible })}"
            >
              <gz-checkbox
                label="${translateFn('Patch Management')}"
                gz-form-input
                @change="${this.checkRestartNotificationPatchStatus}"
              ></gz-checkbox>
            </gz-form-field>
            <gz-text
              id="general-display-restart-patch-text"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText ${classMap({ hidden: !this.isPatchVisible })}"
              >${translateFn(
                'Endpoint restart settings are available in Configuration profiles > Maintenance windows.'
              )}
            </gz-text>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - iSettings.general.display.silentMode
   */
  protected getValueFromManager(): GeneralNotificationsFormValue {
    // get default value
    const defaultValue = getGeneralNotificationsDefaultValue();

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.GENERAL]: {
          display: { silentMode: PolicyPanelManager.getInstance().getPolicy('uiSettings.general.display.silentMode') },
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        general: {
          display: {
            silentMode: {
              disable: this.silentModeDisableField,
              iconNotificationArea: this.showIconEnableField,
              showPopups: {
                enable: this.showPopupsEnableField,
                settings: this.showPopupsSettingsField,
              },
              displayAlertsPopups: this.displayAlertsPopupsEnableField,
              issuesVisibility: {
                enable: this.issueVisibilityEnableField,
                general: this.issueVisibilityGeneralField,
                installationRestartNotifications: this.issueVisibilityGeneralRestartNotificationsField,
                cloudConnectionNotifications: this.issueVisibilityConnectionNotificationsField,
                antimalware: this.issueVisibilityAntimalwareField,
                onAccessNotifications: this.issueVisibilityAntimalwareOnAccessNotificationsField,
                onDemandNotifications: this.issueVisibilityAntimalwareOnDemandNotificationsField,
                showAfter: this.issueVisibilityAntimalwareShowAfterField,
                dissinfectionRestartNotification: this.issueVisibilityAntimalwareRestartNotificationsField,
                firewall: this.issueVisibilityFirewallField,
                contentControl: this.issueVisibilityContentControlField,
                update: this.issueVisibilityUpdateField,
                updateRestartNotifications: this.issueVisibilityUpdateRestartNotificationsField,
              },
              endpointRestartPopup: {
                enable: this.restartEnableField,
                update: this.restartUpdateField,
                patchManagement: this.restartPatchField,
              },
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        general: {
          display: {
            silentMode: {
              disable: !this.valueT.uiSettings.general.display.silentMode.enable,
              iconNotificationArea: this.valueT.uiSettings.general.display.silentMode.iconNotificationArea,
              showPopups: {
                enable: this.valueT.uiSettings.general.display.silentMode.showPopups.enable,
                settings: convertDbValueToFormValue(
                  this.valueT.uiSettings.general.display.silentMode.showPopups.settings
                ),
              },
              displayAlertsPopups: this.valueT.uiSettings.general.display.silentMode.displayAlertsPopups,
              issuesVisibility: this.valueT.uiSettings.general.display.silentMode.issuesVisibility,
              endpointRestartPopup: this.valueT.uiSettings.general.display.silentMode.endpointRestartPopup,
            },
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // set show popups settings
    let showPopupsSettings;
    showPopupsSettings = _.get(value, ['uiSettings', 'general', 'display', 'silentMode', 'showPopups', 'settings'], []);
    showPopupsSettings = convertFormValueToDbValue(showPopupsSettings);
    _.set(value, ['uiSettings', 'general', 'display', 'silentMode', 'showPopups', 'settings'], showPopupsSettings);

    // set profile with backend value
    _.set(
      value,
      ['uiSettings', 'general', 'display', 'silentMode', 'issuesVisibility', 'profile'],
      this.valueT?.uiSettings.general.display.silentMode.issuesVisibility.profile
    );

    // set silentMode
    const isSilentModeDisabled = _.get(value, ['uiSettings', 'general', 'display', 'silentMode', 'disable']);
    _.set(value, ['uiSettings', 'general', 'display', 'silentMode', 'enable'], !isSilentModeDisabled);
    _.unset(value, ['uiSettings', 'general', 'display', 'silentMode', 'disable']);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'general', 'display'], {});
    }

    return value;
  }

  /* General - Notifications - Silent mode region */
  private toggleSilentMode(enable: boolean) {
    toggleDisabled(this.notificationsContent, enable);
    toggleVisibility(this.silentModeEnabledWarning, enable);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    const valuesToCheck = [
      this.showIconEnableField.value,
      this.displayAlertsPopupsEnableField.value,
      this.showPopupsEnableField.value,
      this.issueVisibilityEnableField.value,
      this.restartEnableField.value,
    ];

    // set values to false if silent mode is enabled
    if (enable && valuesToCheck.includes(true)) {
      // save values before changing
      PolicyPanelManager.getInstance().setAdditionalPolicyInfoToPath(['valueHistory', 'general'], {
        iconNotificationArea: this.showIconEnableField.value,
        displayAlertsPopups: this.displayAlertsPopupsEnableField.value,
        showPopups: this.showPopupsEnableField.value,
        issuesVisibility: this.issueVisibilityEnableField.value,
        endpointRestartPopup: this.restartEnableField.value,
      });

      this.changeFieldValueIfNeeded(this.showIconEnableField, false);
      this.changeFieldValueIfNeeded(this.displayAlertsPopupsEnableField, false);
      this.changeFieldValueIfNeeded(this.showPopupsEnableField, false);
      this.changeFieldValueIfNeeded(this.issueVisibilityEnableField, false);
      this.changeFieldValueIfNeeded(this.restartEnableField, false);
    }

    // revert values if silent mode is disabled
    if (!enable && !valuesToCheck.includes(true)) {
      const valueHistory = PolicyPanelManager.getInstance().getAdditionalPolicyInfo(['valueHistory', 'general']);
      this.changeFieldValueIfNeeded(this.showIconEnableField, valueHistory.iconNotificationArea ?? true);
      this.changeFieldValueIfNeeded(this.displayAlertsPopupsEnableField, valueHistory.displayAlertsPopups ?? false);
      this.changeFieldValueIfNeeded(this.showPopupsEnableField, valueHistory.showPopups ?? false);
      this.changeFieldValueIfNeeded(this.issueVisibilityEnableField, valueHistory.issuesVisibility ?? true);
      this.changeFieldValueIfNeeded(this.restartEnableField, valueHistory.endpointRestartPopup ?? false);
    }
  }
  private changeFieldValueIfNeeded(field: GzFormFieldWebComponent, value: boolean) {
    // change value only if needed to avoid unnecessary change events
    if (field.value !== value) {
      field.value = value;
    }
  }
  private async checkSilentModeStatus() {
    await this.updateComplete;
    const valuesToCheck = [
      this.showIconEnableField.value,
      this.displayAlertsPopupsEnableField.value,
      this.showPopupsEnableField.value,
      this.issueVisibilityEnableField.value,
      this.restartEnableField.value,
    ];

    // do nothing if at least one value is true
    if (valuesToCheck.includes(true)) {
      return;
    }

    // set silent mode to true if needed
    this.changeFieldValueIfNeeded(this.silentModeDisableField, false);
  }
  /* General - Notifications - Silent mode end region */

  /* General - Notifications - Show icon region */
  private toggleShowIcon() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkSilentModeStatus();
  }
  /* General - Notifications - Show icon end region */

  /* General - Notifications - Display alert popups region */
  private toggleDisplayAlertPopups(event: CustomEvent) {
    const value = event.detail.checked;

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    // disable restart endpoint popup if display alert popups is disabled
    if (!value && this.restartEnableField.value) {
      this.restartEnableField.value = false;
    }

    this.checkSilentModeStatus();

    if (value) {
      // restore firewall protection level
      PolicyPanelManager.getInstance().restoreFirewallProtectionLevelFromHistory();
      return;
    }

    // set firewall protection level to RULESET_KNOWN_FILES_AND_ALLOW
    PolicyPanelManager.getInstance().setFirewallProtectionLevelToDefaultIfNecessary();
  }
  /* General - Notifications - Display alert popups end region */

  /* General - Notifications - Display notifications popups region */
  private toggleShowPopups(event: CustomEvent) {
    toggleVisibility(this.showPopupsSettingsField, event.detail.checked);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkSilentModeStatus();
  }
  /* General - Notifications - Display notifications popups end region */

  /* General - Notifications - Endpoint issues visibility region */
  private toggleIssueVisibility(event: CustomEvent) {
    const value = event.detail.checked;
    toggleVisibility(this.issueVisibilityContent, value);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    // check if silent mode is enabled
    this.checkSilentModeStatus();

    // keep values displayed in the UI
    if (!value) {
      return;
    }

    // check if at least one scanning option is enabled
    const valuesToCheck = [
      this.issueVisibilityGeneralField.value,
      this.issueVisibilityAntimalwareField.value,
      this.issueVisibilityFirewallField.value,
      this.issueVisibilityContentControlField.value,
      this.issueVisibilityUpdateField.value,
    ];
    if (valuesToCheck.includes(true)) {
      return;
    }

    // if all are disabled, enable fields
    this.issueVisibilityGeneralField.value = value;
    this.issueVisibilityAntimalwareField.value = value;
    this.issueVisibilityFirewallField.value = value;
    this.issueVisibilityUpdateField.value = value;
  }
  private toggleIssueVisibilityGeneralSection(event: CustomEvent) {
    toggleDisabled(this.issueVisibilityGeneralContent, !event.detail.checked);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkIssueVisibilityState();
  }
  private toggleIssueVisibilityAntimalwareSection(event: CustomEvent) {
    toggleDisabled(this.issueVisibilityAntimalwareContent, !event.detail.checked);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkIssueVisibilityState();
  }
  private toggleIssueVisibilityFirewallSection() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkIssueVisibilityState();
  }
  private toggleIssueVisibilityContentControlSection() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkIssueVisibilityState();
  }
  private toggleIssueVisibilityUpdateSection(event: CustomEvent) {
    toggleDisabled(this.issueVisibilityUpdateContent, !event.detail.checked);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkIssueVisibilityState();
  }
  private checkIssueVisibilityState() {
    const valuesToCheck = [
      this.issueVisibilityGeneralField.value,
      this.issueVisibilityAntimalwareField.value,
      this.issueVisibilityFirewallField.value,
      this.issueVisibilityContentControlField.value,
      this.issueVisibilityUpdateField.value,
    ];

    if (valuesToCheck.includes(true)) {
      return;
    }

    this.issueVisibilityEnableField.value = false;
  }
  /* General - Notifications - Endpoint issues visibility end region */

  /* General - Notifications - Endpoint restart notification region */
  private toggleRestartNotification(event: CustomEvent) {
    const value = event.detail.checked;
    toggleVisibility(this.restartContent, value);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    // enable display alerts popups if endpoint restart notification is enabled
    if (value && !this.displayAlertsPopupsEnableField.value) {
      this.displayAlertsPopupsEnableField.value = true;
    }

    // check if silent mode is enabled
    this.checkSilentModeStatus();

    // keep values displayed in the UI
    if (!value) {
      return;
    }

    // check if at least one scanning option is enabled
    const valuesToCheck = [
      this.restartUpdateField.value,
      ...(this.isPatchVisible ? [this.restartPatchField.value] : []),
    ];
    if (valuesToCheck.includes(true)) {
      return;
    }

    // if all are disabled, enable fields
    this.restartUpdateField.value = value;
    if (this.isPatchVisible) {
      this.restartPatchField.value = value;
    }
  }
  private checkRestartNotificationUpdateStatus() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkRestartNotificationStatus();
  }
  private checkRestartNotificationPatchStatus() {
    // wait for component to be ready
    if (!this.isReady || !this.isPatchVisible) {
      return;
    }

    this.checkRestartNotificationStatus();
  }
  private checkRestartNotificationStatus() {
    const valuesToCheck = [
      this.restartUpdateField.value,
      ...(this.isPatchVisible ? [this.restartPatchField.value] : []),
    ];

    if (valuesToCheck.includes(true)) {
      return;
    }

    this.restartEnableField.value = false;
  }
  /* General - Notifications - Endpoint restart notification end region */
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_GENERAL_NOTIFICATIONS: GzGeneralNotificationsWebComponent;
  }
}
